import { ReactNode, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import "./GetFieldUI.css";

export type BorrowerDetailsValues = {
  ssn?: string;
  phone?: string;
  ein?: string;
  entityPhone?: string;
  entityEmail?: string;
  guarantorSsn?: string;
  guarantorPhone?: string;
};

type FieldKey = keyof BorrowerDetailsValues;

type FieldConfig = {
  key: FieldKey;
  label: string;
  placeholder: string;
  validate: (value: string) => boolean;
  errorText: string;
  masked?: boolean;
  isPhone?: boolean;
  format?: (value: string) => string;
};

const digitsOnly = (value: string) => value.replace(/\D/g, "");

const formatSsn = (value: string) => {
  const d = digitsOnly(value).slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const formatEin = (value: string) => {
  const d = digitsOnly(value).slice(0, 9);
  return d.length <= 2 ? d : `${d.slice(0, 2)}-${d.slice(2)}`;
};

const formatPhone = (value: string) => {
  const d = digitsOnly(value).slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const DEFAULT_COUNTRY_CODE = "+1";

// API phones come as "+1 (566) 767-6767": the country code is the digits right
// after the leading "+" (no regex — avoids backtracking concerns).
const countryCodeOf = (value?: string) => {
  const phone = (value ?? "").trim();
  if (!phone.startsWith("+")) return DEFAULT_COUNTRY_CODE;
  let end = 1;
  while (end < phone.length && phone[end] >= "0" && phone[end] <= "9") end++;
  // A bare "+15667676767" has no separator, so cap at 3 digits; the common "+1 (" form stops at the space.
  const code = phone.slice(0, phone[end] === " " || phone[end] === "(" ? end : Math.min(end, 2));
  return code.length > 1 ? code : DEFAULT_COUNTRY_CODE;
};

// The national part of a phone, without its country code.
const nationalPart = (value?: string) => {
  const phone = (value ?? "").trim();
  const d = digitsOnly(phone);
  if (phone.startsWith("+")) return d.slice(countryCodeOf(phone).length - 1);
  return d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
};

export const isValidSsn = (v: string) => /^\d{3}-\d{2}-\d{4}$/.test(v);
export const isValidEin = (v: string) => /^\d{2}-\d{7}$/.test(v);
export const isValidPhone = (v: string) => digitsOnly(v).length === 10;
// Plain string checks instead of one regex: [^\s@]+ next to a literal dot
// backtracks polynomially on long inputs (Sonar S5852).
export const isValidEmail = (v: string) => {
  const email = v.trim();
  // 254 is the max length of a valid email address (RFC 5321).
  if (email.length > 254) return false;
  const at = email.indexOf("@");
  if (at < 1 || at !== email.lastIndexOf("@") || /\s/.test(email)) return false;
  const labels = email.slice(at + 1).split(".");
  return (
    labels.length >= 2 &&
    labels.every((label) => label.length > 0) &&
    labels[labels.length - 1].length >= 2
  );
};

const SSN: Omit<FieldConfig, "key" | "label"> = {
  placeholder: "XXX-XX-XXXX",
  validate: isValidSsn,
  errorText: "Enter a valid SSN (XXX-XX-XXXX)",
  masked: true,
  format: formatSsn,
};
const PHONE: Omit<FieldConfig, "key" | "label"> = {
  placeholder: "(XXX) XXX-XXXX",
  validate: isValidPhone,
  errorText: "Enter a valid 10-digit phone number",
  isPhone: true,
  format: formatPhone,
};

const ENTITY_FIELDS: FieldConfig[] = [
  {
    key: "ein",
    label: "EIN",
    placeholder: "XX-XXXXXXX",
    validate: isValidEin,
    errorText: "Enter a valid EIN (XX-XXXXXXX)",
    format: formatEin,
  },
  { key: "entityPhone", label: "Entity Phone #", ...PHONE },
  {
    key: "entityEmail",
    label: "Entity Email",
    placeholder: "name@example.com",
    validate: isValidEmail,
    errorText: "Enter a valid email address",
  },
];
const GUARANTOR_FIELDS: FieldConfig[] = [
  { key: "guarantorSsn", label: "SSN", ...SSN },
  { key: "guarantorPhone", label: "Guarantor Phone #", ...PHONE },
];
const INDIVIDUAL_FIELDS: FieldConfig[] = [
  { key: "ssn", label: "SSN", ...SSN },
  { key: "phone", label: "Phone #", ...PHONE },
];

const CARD_BORDER_COLOR = "#E4E7EC";

const cardStyle = {
  border: `1px solid ${CARD_BORDER_COLOR}`,
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "16px",
};

type Props = {
  borrowerInformation: any;
  isLoading: boolean;
  onBack: () => void;
  onSubmit: (values: BorrowerDetailsValues) => void;
  // Fetches the full (decrypted) SSN for an existing borrower/guarantor id.
  fetchFullSsn?: (borrowerId: string) => Promise<string>;
  // Extra context (e.g. waiver note, selected program) shown above the warning.
  children?: ReactNode;
};

const last4 = (v?: string) => digitsOnly(v ?? "").slice(-4);
const maskedSsn = (v?: string) => `•••-••-${last4(v)}`;

export default function BorrowerDetailsModalContent({
  borrowerInformation,
  isLoading,
  onBack,
  onSubmit,
  fetchFullSsn,
  children,
}: Readonly<Props>) {
  const isEntity = borrowerInformation?.borrowerType === "Entity";
  const [values, setValues] = useState<BorrowerDetailsValues>(
    () =>
      isEntity
        ? {
            // Pulled from the selected entity (check-borrower-email) into borrowerInformation.
            ein: formatEin(borrowerInformation?.ein ?? ""),
            entityPhone: formatPhone(nationalPart(borrowerInformation?.entityPhone)),
            entityEmail:
              borrowerInformation?.entityEmail ??
              borrowerInformation?.borrowerEmail ??
              "",
            guarantorPhone: formatPhone(
              nationalPart(borrowerInformation?.guarantorPhone),
            ),
          }
        : {
            phone: formatPhone(nationalPart(borrowerInformation?.phone)),
          },
  );
  // Country codes captured from the API phones; sent back with the number.
  const countryCodes: Record<string, string> = {
    entityPhone: countryCodeOf(borrowerInformation?.entityPhone),
    guarantorPhone: countryCodeOf(borrowerInformation?.guarantorPhone),
    phone: countryCodeOf(borrowerInformation?.phone),
  };
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  // Full SSNs fetched via the decrypt API (never sent in the payload — only for display).
  const [decrypted, setDecrypted] = useState<Record<string, string>>({});
  const [fetching, setFetching] = useState<Record<string, boolean>>({});
  const [fetchError, setFetchError] = useState<Record<string, boolean>>({});

  // SSNs the API already returned as last-4 only, and the id to decrypt them with.
  const existingSsn: Record<string, string> = {
    ssn: isEntity ? "" : (borrowerInformation?.ssn ?? ""),
    guarantorSsn: isEntity ? (borrowerInformation?.guarantorSsn ?? "") : "",
  };
  const decryptId: Record<string, string> = {
    ssn: borrowerInformation?.id,
    guarantorSsn: borrowerInformation?.guarantorId,
  };
  // Last-4 from the API, not yet replaced by a user-entered full value.
  const hasExistingSsn = (f: FieldConfig) =>
    Boolean(f.masked) &&
    last4(existingSsn[f.key]).length > 0 &&
    values[f.key] === undefined;

  const allFields = useMemo(
    () =>
      isEntity
        ? [...ENTITY_FIELDS, ...GUARANTOR_FIELDS]
        : INDIVIDUAL_FIELDS,
    [isEntity],
  );

  const isComplete = (f: FieldConfig) =>
    hasExistingSsn(f) || f.validate(values[f.key] ?? "");
  const completeCount = allFields.filter(isComplete).length;
  const allValid = completeCount === allFields.length;

  const individualName =
    borrowerInformation?.borrowerName ||
    [borrowerInformation?.firstName, borrowerInformation?.lastName]
      .filter(Boolean)
      .join(" ");

  // Only send the fields shown for this borrower type (no EIN/guarantor keys for Individual).
  const pickVariantValues = (): BorrowerDetailsValues =>
    Object.fromEntries(
      allFields.map((f) => [
        f.key,
        // An untouched existing SSN is sent as the last-4 the API returned; an
        // edited/new one is sent in full. Phones carry their country code.
        hasExistingSsn(f)
          ? existingSsn[f.key]
          : withCountryCode(f, values[f.key]?.trim() ?? ""),
      ]),
    );

  const withCountryCode = (f: FieldConfig, value: string) =>
    f.isPhone && value ? `${countryCodes[f.key]} ${value}` : value;

  const renderField = (f: FieldConfig) => {
    const existing = hasExistingSsn(f);
    const hidden = f.masked && !revealed[f.key];
    const typed = values[f.key] ?? "";
    const value = existing ? (decrypted[f.key] ?? "") : typed;
    const showError =
      !existing && touched[f.key] && typed !== "" && !f.validate(typed);
    // Once complete (or pre-existing), a hidden SSN shows only its last 4 digits.
    const maskedView = hidden && (existing || f.validate(typed));
    const displayValue = maskedView
      ? maskedSsn(existing ? existingSsn[f.key] : typed)
      : value;
    const toggleReveal = async () => {
      if (revealed[f.key]) {
        setRevealed((prev) => ({ ...prev, [f.key]: false }));
        return;
      }
      const id = decryptId[f.key];
      if (existing && !decrypted[f.key] && id && fetchFullSsn) {
        setFetching((prev) => ({ ...prev, [f.key]: true }));
        setFetchError((prev) => ({ ...prev, [f.key]: false }));
        try {
          const full = formatSsn(await fetchFullSsn(id));
          setDecrypted((prev) => ({ ...prev, [f.key]: full }));
        } catch {
          setFetchError((prev) => ({ ...prev, [f.key]: true }));
          setFetching((prev) => ({ ...prev, [f.key]: false }));
          return;
        }
        setFetching((prev) => ({ ...prev, [f.key]: false }));
      }
      setRevealed((prev) => ({ ...prev, [f.key]: true }));
    };
    const hasError = showError || fetchError[f.key];
    const helperText = (() => {
      if (fetchError[f.key]) return "Unable to load SSN. Please try again.";
      return showError ? f.errorText : " ";
    })();
    const inputId = `borrower-detail-input-${f.key}`;
    let inputProps: any = { disableUnderline: true };
    if (f.isPhone) {
      inputProps = {
        ...inputProps,
        startAdornment: (
          <InputAdornment position="start">{countryCodes[f.key]}</InputAdornment>
        ),
      };
    } else if (f.masked) {
      inputProps = {
        ...inputProps,
        endAdornment: (
          <InputAdornment position="end">
            <Button size="small" disabled={fetching[f.key]} onClick={toggleReveal}>
              {hidden ? "Show" : "Hide"}
            </Button>
          </InputAdornment>
        ),
      };
    }
    // Same look as the sizer's fields (GetFieldUI + common/v2/TextField): the
    // label sits above a borderless "standard" input inside the themed box.
    return (
      <div key={f.key} style={{ flex: 1, minWidth: 0 }}>
        <div className="label-container">
          <div className="label-wrapper">
            <label className="label" htmlFor={inputId} style={{ fontWeight: 600, color: "#3d3a3a" }}>
              {f.label}
              <sup className="label-required">*</sup>
            </label>
          </div>
        </div>
        <div className="mt-8">
          <FormControl variant="outlined" sx={{ width: "100%" }}>
            <TextField
              className={`app-theme-data-points truncate-words p-4 br-6 ${
                hasError ? "app-theme-data-points-error" : ""
              }`}
              // The global theme class forces a grey background with
              // !important; this modal's inputs are white.
              sx={{
                width: "100%",
                "&.app-theme-data-points, &.app-theme-data-points-error": {
                  background: "#fff !important",
                },
                // Same border colour as the card the field sits in
                // (error state keeps its red border).
                "&.app-theme-data-points": {
                  border: `1px solid ${CARD_BORDER_COLOR} !important`,
                },
              }}
              variant="standard"
              size="small"
              id={inputId}
              placeholder={f.placeholder}
              value={displayValue}
              type={hidden && !maskedView ? "password" : "text"}
              error={hasError}
              autoComplete="off"
              onChange={(e) =>
                !maskedView &&
                (!existing || revealed[f.key]) &&
                setValues((prev) => ({
                  ...prev,
                  [f.key]: f.format ? f.format(e.target.value) : e.target.value,
                }))
              }
              onBlur={() => setTouched((prev) => ({ ...prev, [f.key]: true }))}
              inputProps={{ "data-testid": `borrower-detail-${f.key}` }}
              InputProps={inputProps}
            />
            <FormHelperText error={hasError} sx={{ marginLeft: 0 }}>
              {helperText}
            </FormHelperText>
          </FormControl>
        </div>
      </div>
    );
  };

  const renderRow = (fields: FieldConfig[]) => (
    <div style={{ display: "flex", gap: "16px" }}>
      {fields.map(renderField)}
    </div>
  );

  const renderCardHeader = (title: string, detail?: string) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "16px",
        fontSize: "14px",
      }}
    >
      <span style={{ fontWeight: 700, color: "#222265" }}>{title}</span>
      <span style={{ color: "#3C3C3C" }}>{detail}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: "100%" }}>
      <div style={{ fontSize: "20px", fontWeight: 700, lineHeight: "30px" }}>
        Complete borrower details to submit
      </div>
      <p style={{ fontSize: "14px", color: "#3C3C3C", margin: "4px 0 24px" }}>
        LOS requires these before the scenario can be submitted. Fields already
        captured in the sizer are shown for reference.
      </p>

      {isEntity ? (
        <>
          <div style={cardStyle}>
            {renderCardHeader(
              "Borrowing Entity",
              borrowerInformation?.borrowingEntityName,
            )}
            {renderRow(ENTITY_FIELDS.slice(0, 2))}
            {renderField(ENTITY_FIELDS[2])}
          </div>
          <div style={cardStyle}>
            {renderCardHeader(
              "Guarantor",
              [
                borrowerInformation?.guarantorName,
                borrowerInformation?.guarantorEmail,
              ]
                .filter(Boolean)
                .join(" · "),
            )}
            {renderRow(GUARANTOR_FIELDS)}
          </div>
        </>
      ) : (
        <div style={cardStyle}>
          {renderCardHeader(
            "Individual Borrower",
            [individualName, borrowerInformation?.borrowerEmail]
              .filter(Boolean)
              .join(" · "),
          )}
          {renderRow(INDIVIDUAL_FIELDS)}
        </div>
      )}

      {children}

      <div
        data-testid="borrower-details-warning"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          background: "#fff7e2",
          padding: "8px 12px",
          fontSize: "14px",
          lineHeight: "22px",
          color: "#564211",
          marginBottom: "16px",
          fontWeight: 500,
          borderRadius: "4px",
          border: `1.5px solid #f9dd97`,
        }}
      >
        <ReportProblemOutlinedIcon
          data-testid="borrower-details-warning-icon"
          sx={{ color: "#8b7644", fontSize: "22px", flexShrink: 0 }}
        />
        <span>
          By submitting this scenario, you will not be able to make changes to
          the sizer. If you have questions about the sizer or loan scenario,
          click Go Back, then Save, and contact your loan acquisitions
          specialist.
        </span>
      </div>

      <div style={{ border: `1px solid ${CARD_BORDER_COLOR}`, margin: "24px 0px" }}></div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "14px", color: "#3C3C3C" }}>
          {completeCount} of {allFields.length} required fields complete
        </span>
        <div style={{ display: "flex", gap: "12px" }}>
          <Button variant="outlined" disabled={isLoading} onClick={onBack}>
            Go Back
          </Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            disabled={!allValid}
            onClick={() => onSubmit(pickVariantValues())}
          >
            Yes, Submit to LOS
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}
